/**
 * Fulfillment & shipping tiles, for the brand Analytics tab.
 *
 * Rendered as a continuation of the metric grid above it — same Grid, same columnSpan, same
 * MetricCard — so the tab reads as one set of metrics rather than a card grid with a
 * differently-built section stapled underneath. What separates them is a sub-heading, not a
 * change of component: sixteen undifferentiated tiles putting Net Payout beside Missing
 * Tracking is a grid nobody can scan.
 *
 * THESE DESCRIBE THE BILLING ORDERS, the same population as the money tiles above. Built off
 * the connector's own table instead, a brand whose orders do not come through a retailer
 * showed Shipped 0 directly beneath an Orders card reading 4, with all four fulfilled.
 *
 * EVERY TILE FOLLOWS THE PERIOD SELECTOR, but not all on the same date field or the same
 * table, because they cannot be: Shipped and Missing tracking come off the billing rows and
 * are bounded on the fulfilment date (the basis every money tile above them uses), while the
 * backlog tiles come off the connector's rows and are bounded on when the retailer placed the
 * order. Each tooltip names its own basis rather than leaving six tiles looking
 * interchangeable.
 *
 * THE BACKLOG TILES COVER RETAILER ORDERS ONLY, and their tooltips say so. The billing sync
 * is fulfilment-based and discards orders that are not fulfilled, so a brand's own unshipped
 * Shopify orders are not in this system to count; only mirakl_orders knows about an order
 * before it ships. On-time rate and Avg tracking lag are retailer-only for a second reason as
 * well: both measure against timestamps only a retailer supplies, when it placed the order and
 * when we pushed tracking back to it, so on a brand selling direct both read as a dash.
 *
 * DELIVERY IS NOT REPORTED, here or anywhere: nothing in this system receives a delivery
 * confirmation. Every figure below measures how long WE took.
 */

import { Grid, Text, BlockStack, Banner } from '@shopify/polaris';
import {
  PackageIcon, ClockIcon, DeliveryIcon, OrderUnfulfilledIcon,
  ShippingLabelIcon, AlertTriangleIcon,
} from '@shopify/polaris-icons';
import MetricCard from './MetricCard';
import { useBrandLogistics } from '../../utils/queries';

/** The same span every tile on this tab uses, so the two groups line up on one grid. */
const SPAN = { xs: 6, sm: 3, md: 3, lg: 3, xl: 2 };

const fmtLag = (m) => (m == null ? '—' : m < 60 ? `${m} min` : `${(m / 60).toFixed(1)} hrs`);

export default function LogisticsBreakdown({ brandId, params = {} }) {
  const { data, isLoading, error } = useBrandLogistics(brandId, params);

  if (error) {
    return <Banner tone="critical">Unable to load fulfillment data: {error.message}</Banner>;
  }

  const log = data?.logistics ?? {};

  const tile = (props) => (
    <Grid.Cell columnSpan={SPAN}>
      <MetricCard loading={isLoading} error={error} {...props} />
    </Grid.Cell>
  );

  return (
    <BlockStack gap="300">
      <BlockStack gap="050">
        <Text variant="headingMd" as="h3">Fulfillment &amp; shipping</Text>
        <Text variant="bodySm" tone="subdued">
          How fast this brand&rsquo;s orders leave, and which have not. Delivery confirmation is not
          available to this system, so nothing here reports whether a parcel arrived.
        </Text>
      </BlockStack>

      <Grid>
        {tile({
          title: 'Shipped',
          value: log.shipped ?? 0,
          icon: PackageIcon,
          tooltip: 'Orders fulfilled inside the selected period. Counted on the fulfilment date, the same basis as the money tiles above.',
        })}

        {tile({
          title: 'On-time rate',
          value: log.onTimeRate == null ? '—' : `${log.onTimeRate}%`,
          icon: ClockIcon,
          tooltip: log.onTimeRate == null
            ? 'Retailer orders only. No order in this period carries the retailer’s placed-at time, so there is nothing to measure against — a brand selling direct will always read this way.'
            : (log.slaReliable
              ? `Shipped within 24 hours of the retailer placing the order. Based on ${log.onTimeSample} orders whose 24-hour window has closed; orders placed in the last day are still in play and are excluded.`
              : `Retailer orders only. ${log.slaCoverage}% of this period’s orders came through a retailer, so this rate speaks for that share of them and no more.`),
        })}

        {tile({
          title: 'Avg tracking lag',
          value: fmtLag(log.avgTrackingLagMinutes),
          icon: DeliveryIcon,
          tooltip: 'Retailer orders only. Average time between the brand fulfilling and the tracking number reaching the retailer — our lag, not the retailer’s. Reads as a dash when no retailer order shipped in this period.',
        })}

        {tile({
          title: 'Open orders',
          value: log.openOrders ?? 0,
          icon: OrderUnfulfilledIcon,
          tooltip: 'Retailer orders only, placed in this period and still waiting to ship. A brand\u2019s own unfulfilled Shopify orders are not held by this system at all \u2014 the sync keeps only what has shipped \u2014 so they cannot appear here. Orders a retailer cancelled or refused are excluded: they will never ship, so counting them would build a backlog nobody could clear.',
        })}

        {tile({
          title: 'Missing tracking',
          value: log.missingTracking ?? 0,
          icon: ShippingLabelIcon,
          color: log.missingTracking > 0 ? 'critical' : 'default',
          tooltip: 'Fulfilled in this period with no tracking number recorded, from either the retailer or Shopify. Each one is a late-shipment penalty waiting to happen.',
        })}

        {tile({
          title: 'Unshipped over 48h',
          value: log.unshipped48h ?? 0,
          icon: AlertTriangleIcon,
          color: log.unshipped48h > 0 ? 'critical' : 'default',
          tooltip: 'Retailer orders only, placed in this period more than 48 hours ago and still not shipped.',
        })}
      </Grid>
    </BlockStack>
  );
}
